/**
 * Token handling module for authentication
 * Consolidates JWT functionality from the jwt.ts file
 */

import { useFunctionsStore } from '@/store/functions.store';

// Timeouts and limits (ms)
// Increase default validation timeout to better tolerate cold starts; still configurable via env
const VALIDATION_TIMEOUT_MS = Number((import.meta as any).env?.VITE_AUTH_VALIDATION_TIMEOUT_MS ?? 25000);
const REFRESH_TIMEOUT_MS = Number((import.meta as any).env?.VITE_AUTH_REFRESH_TIMEOUT_MS ?? 8000);
const IN_FLIGHT_MAX_AGE_MS = Number((import.meta as any).env?.VITE_AUTH_IN_FLIGHT_MAX_AGE_MS ?? 15000);

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });
}

/**
 * User information extracted from a JWT token
 */
export interface DescopeUserInfo {
  descopeId: string;
  email?: string;
  phone?: string;
  authMethod?: string;
  linkedFirebaseUid?: string;
  [key: string]: any; // Additional fields that might be in the token
}

/**
 * Validation result interface
 */
export interface TokenValidationResult {
  valid: boolean;
  error?: string;
  userInfo?: DescopeUserInfo;
  refreshToken?: string;
}

/**
 * Decode JWT payload safely with error handling
 * 
 * @param token JWT token to decode
 * @returns Decoded payload or null if decoding fails
 */
export function decodeJwt(token: string): any | null {
  try {
    const parts = token.split('.');
    
    // Validate token format
    if (parts.length !== 3) {
      console.warn('JWT token does not have expected 3 parts');
      return null;
    }
    
    const payloadBase64 = parts[1];
    
    // Try standard base64 decoding first
    try {
      const decodedString = atob(payloadBase64);
      return JSON.parse(decodedString);
    } catch (decodeError) {
      console.warn('Standard base64 decoding failed, trying normalized base64');
      
      // Try with normalized base64 (replace URL-safe chars)
      try {
        const normalizedBase64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
        const decodedString = atob(normalizedBase64);
        return JSON.parse(decodedString);
      } catch (fallbackError) {
        console.error('All decoding attempts failed for JWT payload');
        return null;
      }
    }
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Extract user information from a Descope JWT token
 * 
 * @param token JWT token from Descope
 * @param authMethod Optional authentication method to associate with the token
 * @returns User information object or null if extraction fails
 */
export function extractUserInfoFromToken(token: string, authMethod?: string): DescopeUserInfo | null {
  try {
    console.log('Extracting user info from token');
    
    const payload = decodeJwt(token);
    if (!payload) {
      console.error('Failed to decode JWT payload');
      return null;
    }
    
    // Build user info object from payload
    const userInfo: DescopeUserInfo = {
      descopeId: payload.sub || '',
      // Extract standard claims
      email: payload.eml || payload.email || '',
      phone: payload.phn || payload.phone_number || '',
      authMethod: authMethod || (Array.isArray(payload.amr) ? payload.amr[0] : ''),
    };
    
    // Extract Firebase UID if present in claims
    if (payload.firebase_uid || (payload.customClaims && payload.customClaims.firebase_uid)) {
      userInfo.linkedFirebaseUid = payload.firebase_uid || payload.customClaims.firebase_uid;
    }
    
    // Add any additional useful fields from the payload
    const additionalFields = ['given_name', 'family_name', 'name', 'tenant', 'picture'];
    additionalFields.forEach(field => {
      if (payload[field]) {
        userInfo[field] = payload[field];
      }
    });
    
    console.log('Extracted user info:', userInfo);
    return userInfo;
  } catch (error) {
    console.error('Error extracting user info from token:', error);
    return null;
  }
}

/**
 * Store user information from Descope in localStorage
 * 
 * @param userInfo User information to store
 */
export function storeUserInfoInLocalStorage(userInfo: DescopeUserInfo): void {
  if (!userInfo) return;
  
  try {
    if (userInfo.descopeId) {
      localStorage.setItem('descope_user_id', userInfo.descopeId);
      // Also store in webauthnUid for passkey functionality
      localStorage.setItem('webauthnUid', userInfo.descopeId);
    }
    
    if (userInfo.authMethod) {
      localStorage.setItem('descope_auth_method', userInfo.authMethod);
    }
    
    if (userInfo.linkedFirebaseUid) {
      localStorage.setItem('linked_firebase_uid', userInfo.linkedFirebaseUid);
    }
    
    if (userInfo.email) {
      localStorage.setItem('descope_user_email', userInfo.email);
      localStorage.setItem('passkey_login_id', userInfo.email);
    }
    
    if (userInfo.phone) {
      localStorage.setItem('descope_user_phone', userInfo.phone);
      if (!userInfo.email) {
        localStorage.setItem('passkey_login_id', userInfo.phone);
      }
    }
    
    console.log('User info stored in localStorage');
  } catch (error) {
    console.error('Error storing user info in localStorage:', error);
  }
}

/**
 * Process a Descope JWT token to extract and store user information
 * 
 * @param token JWT token from Descope
 * @param authMethod Optional authentication method to associate with the token
 * @returns Extracted user information or null if processing fails
 */
export function processDescopeToken(token: string, authMethod?: string): DescopeUserInfo | null {
  try {
    if (!token) {
      console.error('No token provided to process');
      return null;
    }
    
    // Normalize auth method to expected format
    let normalizedAuthMethod = authMethod;
    if (authMethod === 'email') {
      normalizedAuthMethod = 'otp_email';
    } else if (authMethod === 'sms') {
      normalizedAuthMethod = 'otp_sms';
    } else if (authMethod === 'phone') {
      normalizedAuthMethod = 'otp_sms';
    }
    
    // Store token
    localStorage.setItem('descope_session', token);
    
    // Extract user info from token
    const userInfo = extractUserInfoFromToken(token, normalizedAuthMethod);
    
    if (!userInfo) {
      console.warn('Could not extract user info from token');
      return null;
    }
    
    // Store user info in localStorage
    storeUserInfoInLocalStorage(userInfo);
    
    return userInfo;
  } catch (error) {
    console.error('Error processing Descope token:', error);
    return null;
  }
}

/**
 * Validate a Descope token with the server
 * This ensures the token is valid and hasn't been revoked
 * 
 * @param token Descope token to validate
 * @returns Promise resolving to validation result
 */
export async function validateDescopeToken(token: string, attemptRefresh = false): Promise<TokenValidationResult> {
  try {
    if (!token) {
      return {
        valid: false,
        error: 'No token provided for validation'
      };
    }
    
    // Call the server-side function to validate the token
    const functionsStore = useFunctionsStore();
    
    // One-shot retry on timeout to mitigate transient cold starts
    const execValidation = async (): Promise<any> => withTimeout(
      functionsStore.callOnRequest(
        'authenticateOnRequestAuthenticateWithDescope',
        { sessionToken: token }
      ),
      VALIDATION_TIMEOUT_MS,
      'Token validation'
    );

    let response: any;
    try {
      response = await execValidation();
    } catch (e: any) {
      const msg = e?.message || String(e);
      if (msg.includes('timed out')) {
        console.warn('[TokenValidation] Initial validation timed out, retrying once...');
        response = await execValidation();
      } else {
        throw e;
      }
    }
    
    if (!response || !response.success) {
      console.error('Token validation failed:', response?.error || 'Unknown error');
      
      // If validation failed and we haven't tried refresh yet, attempt it
      // Check for token expiration errors (both 'token_expired' and 'JWTExpired')
      const isTokenExpiredError = response?.error?.includes('token_expired') || 
                                  response?.error?.includes('JWTExpired') ||
                                  response?.error?.includes('exp') ||
                                  response?.error?.includes('expired');
      
      if (attemptRefresh && isTokenExpiredError) {
        console.log('Attempting token refresh after validation failure...');
        const refreshResult = await attemptTokenRefresh();
        if (refreshResult.success) {
          // Recursively validate the new token (without retry to avoid infinite loops)
          return validateDescopeToken(refreshResult.token!, false);
        }
      }
      
      return {
        valid: false,
        error: response?.error || 'Token validation failed'
      };
    }
    
    // Extract user info from the token
    const extractedUserInfo = processDescopeToken(token);
    
    // If a refresh token was provided, store it
    if (response.refreshToken) {
      localStorage.setItem('descope_refresh_token', response.refreshToken);
    }
    
    return {
      valid: true,
      userInfo: extractedUserInfo || undefined,
      refreshToken: response.refreshToken
    };
  } catch (error) {
    console.error('Error validating token:', error);
    
    // If validation threw an error and we can attempt refresh, try it
    // Check if the error indicates token expiration
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isTokenExpiredError = errorMessage.includes('JWTExpired') || 
                               errorMessage.includes('token_expired') ||
                               errorMessage.includes('expired') ||
                               errorMessage.includes('exp');
    
    if (attemptRefresh && isTokenExpiredError) {
      console.log('Attempting token refresh after validation error...');
      const refreshResult = await attemptTokenRefresh();
      if (refreshResult.success) {
        // Recursively validate the new token (without retry to avoid infinite loops)
        return validateDescopeToken(refreshResult.token!, false);
      }
    }
    
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Unknown error validating token'
    };
  }
}

/**
 * Interface for token refresh results
 */
export interface TokenRefreshResult {
  success: boolean;
  token?: string;
  error?: string;
  isNewSession?: boolean;
}

/**
 * Attempt to refresh the current session token
 */
export async function attemptTokenRefresh(): Promise<TokenRefreshResult> {
  try {
    console.log('[TokenRefresh] Attempting to refresh session token...');
    
    // Check for refresh token first
    const refreshToken = localStorage.getItem('descope_refresh_token');
    if (refreshToken) {
      console.log('[TokenRefresh] Using stored refresh token');
      const refreshResult = await refreshWithRefreshToken(refreshToken);
      if (refreshResult.success) {
        return refreshResult;
      }
    }
    
    // Try to refresh using Descope SDK session management
    const { getSdk } = await import('@descope/vue-sdk');
    const descopeSdk = getSdk();
    
    if (!descopeSdk) {
      return {
        success: false,
        error: 'Descope SDK not available for token refresh'
      };
    }
    
    // Check if there's an active session token to refresh
    const currentToken = localStorage.getItem('descope_session');
    if (!currentToken) {
      console.warn('[TokenRefresh] No active session to refresh');
      return {
        success: false,
        error: 'No active session to refresh'
      };
    }
    
    // Attempt to refresh the session
    try {
      const refreshResponse = await withTimeout(
        descopeSdk.refresh(),
        REFRESH_TIMEOUT_MS,
        'Session refresh'
      );
      if (refreshResponse.ok && refreshResponse.data?.sessionJwt) {
        const newToken = refreshResponse.data.sessionJwt;
        
        // Store the new token
        localStorage.setItem('descope_session', newToken);
        
        // Clear token validation cache since we have a new token
        clearTokenValidationCache();
        
        // Update user info if available
        if (refreshResponse.data.user?.userId) {
          localStorage.setItem('descope_user_id', refreshResponse.data.user.userId);
          localStorage.setItem('webauthnUid', refreshResponse.data.user.userId);
        }
        
        console.log('[TokenRefresh] Session token refreshed successfully');
        return {
          success: true,
          token: newToken,
          isNewSession: false
        };
      }
    } catch (refreshError) {
      console.warn('[TokenRefresh] SDK refresh failed:', refreshError);
    }
    
    // If all refresh attempts failed, the session is truly expired
    console.error('[TokenRefresh] All token refresh attempts failed');
    return {
      success: false,
      error: 'Session expired - all refresh attempts failed'
    };
    
  } catch (error) {
    console.error('[TokenRefresh] Token refresh error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Token refresh failed'
    };
  }
}

/**
 * Refresh session using a stored refresh token
 */
export async function refreshWithRefreshToken(refreshToken: string): Promise<TokenRefreshResult> {
  try {
    const functionsStore = useFunctionsStore();
    
    const sessionToken = localStorage.getItem('descope_session');
    const response = await functionsStore.callOnRequest(
      'authenticateOnRequestRefreshSession',
      { refreshToken, sessionToken }
    );
    
    console.log('[TokenRefresh] Server response:', response);

    if (response?.success && response.sessionToken) {
      // Store the new session token
      localStorage.setItem('descope_session', response.sessionToken);
      
      // Clear token validation cache since we have a new token
      clearTokenValidationCache();
      
      // Store new refresh token if provided
      if (response.refreshToken) {
        localStorage.setItem('descope_refresh_token', response.refreshToken);
      }
      
      // Update user info if provided
      if (response.userId) {
        localStorage.setItem('descope_user_id', response.userId);
        localStorage.setItem('webauthnUid', response.userId);
      }
      
      console.log('[TokenRefresh] Refresh token used successfully');
      return {
        success: true,
        token: response.sessionToken,
        isNewSession: false
      };
    }
    
    // If refresh token failed, remove it
    localStorage.removeItem('descope_refresh_token');
    
    return {
      success: false,
      error: response?.error || 'Refresh token is invalid or expired'
    };
    
  } catch (error) {
    console.error('[TokenRefresh] Refresh token error:', error);
    
    // Remove invalid refresh token
    localStorage.removeItem('descope_refresh_token');
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Refresh token failed'
    };
  }
}

// Cache for recent token validations to avoid excessive server calls
interface TokenValidationCache {
  token: string;
  timestamp: number;
  result: TokenValidationResult;
}

let lastValidationCache: TokenValidationCache | null = null;

// Persisted cache key for cross-tab/session reuse
const PERSISTED_VALIDATION_KEY = 'descope_validation_cache_v1';

// In-flight validation de-duplication
interface InFlightEntry {
  promise: Promise<TokenValidationResult & { refreshed?: boolean }>;
  startedAt: number;
}

const inFlightValidationByToken = new Map<string, InFlightEntry>();

function getCacheTtlMs(): number {
  const minutes = Number(import.meta.env.VITE_AUTH_VALIDATION_CACHE_MINUTES || 30);
  return Math.max(1, minutes) * 60 * 1000;
}

function getTokenSignature(token: string): string {
  try {
    // Use a short, non-sensitive signature to avoid storing full token
    const head = token.slice(0, 12);
    const tail = token.slice(-12);
    return `${head}..${tail}`;
  } catch {
    return 'sig';
  }
}

function readPersistedValidation(token: string): TokenValidationResult | null {
  try {
    const raw = localStorage.getItem(PERSISTED_VALIDATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const ttl = getCacheTtlMs();
    if (parsed && parsed.sig === getTokenSignature(token) && typeof parsed.ts === 'number' && (now - parsed.ts) < ttl) {
      const userInfo = processDescopeToken(token) || undefined;
      return { valid: true, userInfo };
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedValidation(token: string): void {
  try {
    const payload = { sig: getTokenSignature(token), ts: Date.now() };
    localStorage.setItem(PERSISTED_VALIDATION_KEY, JSON.stringify(payload));
  } catch {}
}

/**
 * Enhanced token validation with automatic refresh
 * This is the main function to use for session validation
 */
export async function validateSessionWithRefresh(forceValidation = false): Promise<TokenValidationResult & { refreshed?: boolean }> {
  const token = localStorage.getItem('descope_session');
  if (!token) {
    return {
      valid: false,
      error: 'No session token available'
    };
  }

  const now = Date.now();
  const cacheValidityMs = getCacheTtlMs();

  // In-memory cache hit
  if (!forceValidation && lastValidationCache &&
      lastValidationCache.token === token &&
      (now - lastValidationCache.timestamp) < cacheValidityMs &&
      lastValidationCache.result.valid) {
    console.log('[TokenValidation] Using cached validation result (memory)');
    return { ...lastValidationCache.result, refreshed: false };
  }

  // Persisted cache hit (cross-tab/session)
  if (!forceValidation) {
    const persisted = readPersistedValidation(token);
    if (persisted?.valid) {
      // Update in-memory cache for faster subsequent checks
      lastValidationCache = {
        token,
        timestamp: now,
        result: { valid: true, userInfo: persisted.userInfo }
      };
      console.log('[TokenValidation] Using cached validation result (persisted)');
      return { valid: true, userInfo: persisted.userInfo, refreshed: false };
    }
  }

  // De-duplicate concurrent validations for the same token
  const existing = inFlightValidationByToken.get(token);
  if (existing) {
    const age = now - existing.startedAt;
    if (age < IN_FLIGHT_MAX_AGE_MS) {
      return existing.promise;
    }
    // Stale/hung validation - drop and start a new one
    inFlightValidationByToken.delete(token);
    console.warn('[TokenValidation] Dropped stale in-flight validation (age ms):', age);
  }

  const exec = (async () => {
    // Client-side expiration check first
    if (isTokenExpiredClientSide(token)) {
      console.log('[TokenValidation] Token appears expired client-side, attempting refresh...');
      const refreshResult = await attemptTokenRefresh();
      if (refreshResult.success && refreshResult.token) {
        const newUserInfo = processDescopeToken(refreshResult.token);
        const validationResult = { valid: true, userInfo: newUserInfo || undefined, refreshed: true } as const;
        // Cache + persist
        lastValidationCache = {
          token: refreshResult.token,
          timestamp: Date.now(),
          result: { valid: true, userInfo: newUserInfo || undefined }
        };
        writePersistedValidation(refreshResult.token);
        return { ...validationResult };
      } else {
        lastValidationCache = null;
        return { valid: false, error: refreshResult.error || 'Token expired and refresh failed', refreshed: false };
      }
    }

    // Server validation with auto-refresh
    const result = await validateDescopeToken(token, true);
    if (result.valid) {
      lastValidationCache = {
        token: localStorage.getItem('descope_session') || token,
        timestamp: Date.now(),
        result: { valid: result.valid, userInfo: result.userInfo, error: result.error }
      };
      writePersistedValidation(token);
    } else {
      lastValidationCache = null;
    }

    if (result.valid && result.userInfo) {
      const currentToken = localStorage.getItem('descope_session');
      const wasRefreshed = currentToken !== token;
      return { ...result, refreshed: wasRefreshed };
    }
    return result;
  })();

  inFlightValidationByToken.set(token, { promise: exec, startedAt: now });
  try {
    return await exec;
  } finally {
    inFlightValidationByToken.delete(token);
  }
}

/**
 * Clear the token validation cache
 * Use this when you know the token state has changed externally
 */
export function clearTokenValidationCache(): void {
  lastValidationCache = null;
  console.log('[TokenValidation] Cache cleared');
}

/**
 * Reset any in-flight validations (e.g., after tab resumes) to prevent hangs
 */
export function resetInFlightValidations(): void {
  if (inFlightValidationByToken.size > 0) {
    console.warn('[TokenValidation] Resetting in-flight validations');
  }
  inFlightValidationByToken.clear();
}

/**
 * Check if a token appears to be expired based on its payload (client-side check)
 * This is a preliminary check and should be followed by server validation
 */
export function isTokenExpiredClientSide(token: string): boolean {
  try {
    if (!token) return true;
    
    // Decode the JWT payload directly to get the exp claim
    const payload = decodeJwt(token);
    if (!payload?.exp) return false; // Can't determine expiration
    
    const now = Math.floor(Date.now() / 1000);
    // Allow configuring the proactive refresh buffer. Defaults to 180s.
    const configured = Number((import.meta as any).env?.VITE_AUTH_EXPIRY_BUFFER_SECONDS ?? 180);
    const bufferSeconds = Number.isFinite(configured) ? Math.min(Math.max(configured, 0), 600) : 180;
    
    return payload.exp <= (now + bufferSeconds);
  } catch {
    return false; // If we can't parse it, assume it's valid and let server validation handle it
  }
} 